// Helper for attaching auth to native browser WebSocket connections.
//
// The browser `WebSocket` constructor cannot set custom request headers, so we
// cannot send a real `Authorization: Bearer <token>` header on the handshake.
// The standard workaround is to smuggle the bearer token via the
// `Sec-WebSocket-Protocol` subprotocol header, which IS settable by passing a
// second argument to `new WebSocket(url, protocols)`.
//
// Wire format (must match the backend's parser):
//   Sec-WebSocket-Protocol: Authorization, Bearer.<jwt>
//
// Notes:
// - Subprotocol tokens follow RFC 6455 token grammar (no spaces, no commas).
//   We use a literal dot between "Bearer" and the JWT instead of a space.
// - The server MUST echo one of the offered subprotocols back on the 101
//   response (e.g. `await ws.accept(subprotocol="Authorization")`), otherwise
//   the browser fails the handshake with "subprotocol not supported".
// - For IDM builds we return `undefined` so the WebSocket falls back to the
//   existing cookie-based handshake (the browser auto-attaches cookies on
//   same-origin upgrade requests).

import { isCognitoLoginAllowed } from "src/config/loginMethods";
import { ensureValidCognitoToken } from "./cognitoAuth";

/**
 * Returns the `protocols` argument to pass to `new WebSocket(url, protocols)`.
 * - Cognito session: `["Authorization", "Bearer.<jwt>"]`
 * - IDM session (or no token available): `undefined` — caller should construct
 *   the socket without a second argument so cookies authenticate the handshake.
 */
export const getWebSocketAuthProtocols = async (): Promise<string[] | undefined> => {
	if (!isCognitoLoginAllowed) return undefined;

	const token = await ensureValidCognitoToken();
	if (!token) return undefined;

	return ["Authorization", `Bearer.${token}`];
};
